import {endDrawer} from '@/components/drawers';
import {getFeatureFlag} from '@/feature-flags/feature-flags-store';
import {VariantState} from '@/product/types';
import {logError, logInfo} from '@/utilities/log';
import {Plan} from '@rechargeapps/storefront-client';
import {
  addLineItems,
  checkout,
  getCart,
  getDonationCategory,
  updateLineItem,
} from './cart-store';
import {isRecurringLineItem} from './line-item';
import {CartLineItem, UpdateLineItemInput} from './types';
import {useNavigate} from '@/hooks/use-navigate';

/**
 * Update the quantity of a line item in the cart.
 */
export async function handleUpdateQuantity(line: number, quantity: number) {
  await updateLineItem({
    line,
    quantity,
  });
}

/**
 * Updates the selling plan for a specific line item in the cart.
 *
 * @param line - The line number of the item to update.
 * @param plan - The selling plan to apply to the item.
 * @returns A promise that resolves when the update is complete.
 */
export async function handleUpdateSellingPlan(line: number, plan: Plan | null) {
  const cart = getCart();
  const item = cart?.items[line - 1];

  await updateLineItem({
    line,
    selling_plan: plan?.external_plan_id ?? undefined,
    quantity: item?.quantity ?? 1,
  });
}

/**
 * Handles the checkout process for the shopping cart with donation options.
 * If the donation category is not set, it defaults to the first option.
 * @param donationOptions - An array of donation options.
 */
export async function handleCheckout(donationOptions: string[]) {
  try {
    // If the donation category is not set, default to the first option
    const category = getDonationCategory() ?? donationOptions[0];
    await setDonationCategory(category);
    await checkout();
  } catch (error) {
    logError(error);
  }
}

/**
 * Save the donation category to the cart attributes.
 * @deprecated Earth Breeze will no longer offer the option to select donation category. All logic related to donation categories should be deprecated from the site.
 */
export async function handleDonationUpdate(option: string) {
  try {
    await setDonationCategory(option);
  } catch (error) {
    logError(error, {donationCategory: option});
  }
}

/**
 * Sets the donation category for the items in the cart.
 *
 * @deprecated Earth Breeze will no longer offer the option to select donation category. All logic related to donation categories should be deprecated from the site.
 * @param category - The donation category to set.
 * @returns A promise that resolves when the donation category is successfully updated.
 * @throws If there is an error updating the donation category.
 */
async function setDonationCategory(category: string) {
  const cart = getCart();
  if (!cart) return;
  try {
    await Promise.all(
      cart.items.map((item) =>
        updateLineItem({
          id: item.id.toString(),
          // quantity must be specified, otherwise Shopify resets it to 1
          quantity: item.quantity,
          properties: {
            _buy_one_give_ten_category: category,
          },
        })
      )
    );
    logInfo('DonationCategoryUpdated', {category, cart});
  } catch (error) {
    logError(error, {message: 'ErrorUpdatingDonationCategory', category, cart});
    throw error;
  }
}

/**
 * Handles adding a recommended product to the cart.
 *
 * @param variant - The variant of the recommended product.
 * @param plan - The selling plan for the recommended product (optional).
 */
export async function handleAddRecommendedProduct(
  variant: VariantState,
  plan?: Plan | null
) {
  try {
    await addLineItems([
      {
        id: parseInt(variant.id),
        quantity: 1,
        selling_plan: plan?.external_plan_id ?? undefined,
      },
    ]);
    logInfo('AddedRecommendedProduct', {variant, plan});
  } catch (error) {
    logError(error, {
      message: 'ErrorAddingRecommendedProduct',
      variant,
      plan,
    });
  }
}

/**
 * Swaps an upsell item in the cart.
 * If a subscription item in the cart has the same product ID as the upsell item,
 * the subscription item is removed and the upsell item is added.
 * Otherwise, the upsell item is added to the cart.
 *
 * @param items - The current items in the cart.
 * @param plan - The selling plan for the upsell item.
 * @param variant - The variant of the upsell item.
 */
export async function swapUpsellItem({
  items,
  plan,
  variant,
}: SwapUpsellItemInput) {
  try {
    const replaceItemLine = items.findIndex(
      (item) =>
        isRecurringLineItem(item) &&
        item.product_id.toString() === variant.product.id.toString()
    );

    // Both promises can be awaited in parallel
    // the cart store will ensure that the order is preserved
    // this allows us to display 'optimistic' cart updates to the user
    const promises: Promise<unknown>[] = [];

    // Remove the 'replaced' item if it exists
    if (replaceItemLine >= 0) {
      promises.push(
        updateLineItem({
          line: replaceItemLine + 1,
          quantity: 0,
        })
      );
    }

    // Add the upsell item
    promises.push(
      addLineItems([
        {
          id: parseInt(variant.id),
          quantity: 1,
          selling_plan: plan?.external_plan_id ?? undefined,
        },
      ])
    );

    await Promise.all(promises);

    logInfo('AcceptedCartUpsell', {variant, plan, replaceItemLine, items});
  } catch (error) {
    logError(error, {message: 'ErrorAcceptingUpsell', variant, plan});
  }
}

export type SwapUpsellItemInput = {
  variant: VariantState;
  plan: Plan | null;
  items: CartLineItem[];
};

/**
 * Handles adding a product variant to the cart.
 *
 * @param variant - The selected variant to add to the cart.
 * @param plan - The selected plan for the variant.
 * @returns A Promise that resolves when the variant is successfully added to the cart.
 * @throws An error if no variant is selected.
 */
export async function handleAddToCart(
  variant: VariantState | null,
  plan: Plan | null,
  cartShouldOpen = true
) {
  try {
    if (!variant) {
      throw new Error('No variant selected');
    }

    // The donation category should match the other cart items
    // This ensures correct merge behavior if the same product is added multiple times
    const donationCategory = getDonationCategory();
    const properties = donationCategory
      ? {
          _buy_one_give_ten_category: donationCategory,
        }
      : undefined;

    const addLineItemsPromise = addLineItems([
      {
        id: parseInt(variant?.id ?? ''),
        quantity: 1,
        selling_plan: plan?.external_plan_id ?? undefined,
        properties,
      },
    ]);

    if (cartShouldOpen) openCart();

    await addLineItemsPromise;

    if (!plan) {
      logInfo('Warn: NoPlanSelected', {variant, plan});
    }
  } catch (error) {
    logError(error, {
      message: 'ErrorAddingToCart',
      variant,
      plan,
    });
  }
}

/**
 * Opens the cart page or the end drawer based on the value of the 'cart-page' feature flag.
 */
function openCart() {
  const cartPageFlag = getFeatureFlag('cart-page');
  const postAtcUpsellFlag = getFeatureFlag('post-atc');

  // Don't open the cart drawer if the post-atc feature flag is enabled
  if (postAtcUpsellFlag === 'true') {
    return;
  }

  if (cartPageFlag === 'true') {
    // useNavigate is safe to use outside of a component
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const navigate = useNavigate();
    navigate('/cart');
    return;
  }

  endDrawer.open();
}

export async function updateAllSellingPlans(
  variants: number[],
  sellingPlanId: number | null
): Promise<void> {
  const cart = getCart();
  if (!cart) {
    return;
  }

  const items: UpdateLineItemInput[] = [];

  for (const item of cart.items) {
    if (!variants.includes(item.id)) {
      continue;
    }

    const planId = item.selling_plan_allocation?.selling_plan.id;

    if (!sellingPlanId && !planId) {
      continue;
    }

    if (sellingPlanId === planId) {
      continue;
    }

    items.push({
      id: item.id.toString(),
      selling_plan: sellingPlanId,
      quantity: item.quantity,
    });
  }

  if (items.length === 0) {
    return;
  }

  await Promise.all(items.map((i) => updateLineItem(i)));
}
